<template>
  <div class="home">
    <!-- 资源列表页面 - Updated -->
    <!-- 分类导航 -->
    <div class="category-nav">
      <div 
        class="category-item" 
        :class="{active: categoryId === null}"
        @click="selectCategory(null)">
        <i class="el-icon-s-home category-icon-all"></i>
        <span>全部</span>
      </div>
      <div 
        v-for="cat in categories" 
        :key="cat.id"
        class="category-item"
        :class="{active: categoryId === cat.id}"
        @click="selectCategory(cat.id)">
        <i :class="getCategoryIcon(cat.name)" :style="{color: getCategoryColor(cat.name)}"></i>
        <span>{{ cat.name }}</span>
      </div>
    </div>

    <!-- 资源列表 -->
    <transition name="fade">
      <div v-if="displayMode === 'list' && !loading" class="resource-list" :class="{ 'two-columns': listColumns === 2 }">
      <div 
        v-for="item in resourceList" 
        :key="item.id"
        class="resource-item"
        @click="goToDetail(item.id)">
        <div class="top-corner-badge" v-if="item.isTop === 1">
          <i class="el-icon-star-on"></i>
        </div>
        
        <!-- 标题区域 -->
        <div class="item-header">
          <h3 class="item-title">{{ item.title }}</h3>
          <div class="item-tags">
            <el-tag v-if="item.requiredMembership > 0" type="warning" size="small" effect="dark">
              <i class="el-icon-trophy"></i> 
              {{ ['', '高级会员', '专业会员'][item.requiredMembership] }}
            </el-tag>
            <el-tag v-if="item.isFree === 1" type="success" size="small">免费</el-tag>
            <el-tag v-else type="warning" size="small">¥{{ item.price }}</el-tag>
            <el-tag type="info" size="small">{{ getResourceType(item.resourceType) }}</el-tag>
            <el-tag
              v-for="(tag, index) in (item.tags ? item.tags.split(',').slice(0, 2) : [])"
              :key="tag"
              size="small"
              type="success"
              class="resource-tag"
            >
              {{ tag }}
            </el-tag>
            <div class="more-tags" v-if="item.tags && item.tags.split(',').length > 2">
              +{{ item.tags.split(',').length - 2 }}
            </div>
          </div>
        </div>
        
        <!-- 内容区域：图片和描述左右排列 -->
        <div class="item-body">
          <!-- 封面图 -->
          <div class="item-cover">
            <img :src="getCoverImage(item.coverImage)" alt="">
          </div>
          
          <!-- 描述和统计信息 -->
          <div class="item-content">
            <p class="item-desc">{{ item.description }}</p>
            
            <div class="item-footer">
              <div class="item-stats">
                <span class="stat-item">
                  <i class="el-icon-view"></i> {{ item.viewCount }}
                </span>
                <span class="stat-item">
                  <i class="el-icon-download"></i> {{ item.downloadCount }}
                </span>
                <span class="stat-item">
                  <i class="el-icon-star-off"></i> {{ item.likeCount }}
                </span>
              </div>
              <div class="item-time">
                <i class="el-icon-time"></i> {{ formatDate(item.createTime) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </transition>

    <!-- 网格模式 -->
    <transition name="fade">
      <div v-if="displayMode !== 'list' && !loading" class="resource-grid" :style="gridStyle">
      <div 
        v-for="item in resourceList" 
        :key="item.id"
        class="resource-card"
        @click="goToDetail(item.id)">
        
        <!-- 置顶标签 -->
        <div class="top-badge-card" v-if="item.isTop === 1">
          <i class="el-icon-star-on"></i>
        </div>
        
        <!-- 封面图 -->
        <div class="card-cover">
          <img :src="getCoverImage(item.coverImage)" alt="">
          <div class="cover-overlay">
            <i class="el-icon-view"></i>
            <span>查看详情</span>
          </div>
        </div>
        
        <!-- 卡片内容 -->
        <div class="card-content">
          <h3 class="card-title">{{ item.title }}</h3>
          <p class="card-desc">{{ item.description }}</p>
          
          <div class="card-footer">
            <div class="card-tags">
              <el-tag v-if="item.requiredMembership > 0" type="warning" size="mini" effect="dark">
                <i class="el-icon-trophy"></i> VIP
              </el-tag>
              <el-tag v-if="item.isFree === 1" type="success" size="mini">免费</el-tag>
              <el-tag v-else type="warning" size="mini">¥{{ item.price }}</el-tag>
              <el-tag type="info" size="mini">{{ getResourceType(item.resourceType) }}</el-tag>
              <el-tag
                v-for="(tag, index) in (item.tags ? item.tags.split(',').slice(0, 2) : [])"
                :key="tag"
                size="mini"
                type="success"
                class="resource-tag"
              >
                {{ tag }}
              </el-tag>
              <div class="more-tags-mini" v-if="item.tags && item.tags.split(',').length > 2">
                +{{ item.tags.split(',').length - 2 }}
              </div>
            </div>
            <div class="card-stats">
              <span class="stat-item">
                <i class="el-icon-view"></i> {{ item.viewCount }}
              </span>
              <span class="stat-item">
                <i class="el-icon-download"></i> {{ item.downloadCount }}
              </span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </transition>

    <!-- 加载中提示 -->
    <div v-if="loading" style="text-align: center; padding: 60px 0; color: #909399;">
      <i class="el-icon-loading" style="font-size: 32px;"></i>
      <p style="margin-top: 16px;">加载中...</p>
    </div>

    <!-- 空状态 -->
    <el-empty v-if="!loading && resourceList.length === 0" description="暂无资源"></el-empty>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="total > 0">
      <el-pagination
        @current-change="handlePageChange"
        :current-page="currentPage"
        :page-size="pageSize"
        layout="total, prev, pager, next, jumper"
        :total="total"
        background>
      </el-pagination>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      categories: [],
      resourceList: [],
      categoryId: null,
      keyword: '',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      displayMode: 'list', // 强制使用列表模式
      listColumns: 1, // 列表列数
      gridColumns: 3 // 网格列数
    }
  },
  watch: {
    '$route.query.keyword'(val) {
      this.keyword = val || ''
      this.currentPage = 1
      this.loadResources()
    }
  },
  mounted() {
    // 从URL查询参数获取搜索关键词
    this.keyword = this.$route.query.keyword || ''
    // 新增：优先应用缓存的显示配置，避免首屏切换造成抖动
    this.loadCachedDisplayConfig()
    this.loadDisplayConfig()
    this.loadCategories()
    this.loadResources()
  },
  methods: {
    async loadDisplayConfig() {
      try {
        const res = await this.$axios.get('/config/display')
        console.log('配置接口返回:', res.data)
        if (res.data.code === 200) {
          this.displayMode = res.data.data.displayMode || 'list'
          this.listColumns = parseInt(res.data.data.listColumns) || 1
          this.gridColumns = parseInt(res.data.data.gridColumns) || 3
          // 新增：缓存显示配置
          localStorage.setItem('display_config_cache', JSON.stringify({
            displayMode: this.displayMode,
            listColumns: this.listColumns,
            gridColumns: this.gridColumns
          }))
          console.log('当前显示模式:', this.displayMode)
          console.log('列表列数:', this.listColumns)
          console.log('网格列数:', this.gridColumns)
        }
      } catch (error) {
        console.error('加载配置失败:', error)
        // 配置加载失败时，强制使用列表模式
        this.displayMode = 'list'
        this.listColumns = 1
      }
    },
    // 新增：从缓存预加载显示配置，减少首屏切换
    loadCachedDisplayConfig() {
      try {
        const cached = localStorage.getItem('display_config_cache')
        if (cached) {
          const cfg = JSON.parse(cached)
          this.displayMode = cfg.displayMode || 'list'
          this.listColumns = parseInt(cfg.listColumns) || 1
          this.gridColumns = parseInt(cfg.gridColumns) || 3
        }
      } catch (e) {
        // 忽略缓存解析错误
      }
    },
    async loadCategories() {
      const res = await this.$axios.get('/category/list')
      if (res.data.code === 200) {
        this.categories = res.data.data
      }
    },
    async loadResources() {
      this.loading = true
      try {
        const res = await this.$axios.get('/resource/page', {
          params: {
            current: this.currentPage,
            size: this.pageSize,
            categoryId: this.categoryId,
            keyword: this.keyword
          }
        })
        if (res.data.code === 200) {
          this.resourceList = res.data.data.records
          this.total = res.data.data.total
        }
      } finally {
        this.loading = false
      }
    },
    selectCategory(id) {
      this.categoryId = id
      this.currentPage = 1
      // 切换分类时清除搜索关键词
      this.keyword = ''
      // 清除URL中的keyword参数
      if (this.$route.query.keyword) {
        this.$router.replace({ path: '/', query: {} }).catch(() => {})
      }
      this.loadResources()
    },
    handleCategoryClick(id) {
      this.categoryId = id
      this.currentPage = 1
      // 切换分类时清除搜索关键词
      this.keyword = ''
      // 清除URL中的keyword参数
      if (this.$route.query.keyword) {
        this.$router.replace({ path: '/', query: {} }).catch(err => {
          // 忽略NavigationDuplicated错误，但重新抛出其他错误
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
      this.loadResources()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadResources()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    goToDetail(id) {
      // 避免重复导航错误
      const targetPath = `/resource/${id}`
      if (this.$route.path !== targetPath) {
        this.$router.push(targetPath).catch(err => {
          // 忽略NavigationDuplicated错误，但重新抛出其他错误
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
    },
    getResourceType(type) {
      const map = { 0: '软件', 1: '网站', 2: '视频教程', 3: '图文教程', 4: '其他' }
      return map[type] || '未知'
    },
    getCoverImage(url) {
      if (!url) return '/images/default-cover.png'  // 使用项目中的默认图片
      if (url.startsWith('http')) return url
      if (!url.startsWith('/')) return '/' + url
      return url
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      
      if (days === 0) return '今天'
      if (days === 1) return '昨天'
      if (days < 7) return `${days}天前`
      if (days < 30) return `${Math.floor(days / 7)}周前`
      if (days < 365) return `${Math.floor(days / 30)}月前`
      return `${Math.floor(days / 365)}年前`
    },
    getCategoryIcon(categoryName) {
      const iconMap = {
        '电脑软件': 'el-icon-monitor',
        '手机软件': 'el-icon-mobile-phone',
        '精品网站': 'el-icon-link',
        '视频教程': 'el-icon-video-camera',
        '图文教程': 'el-icon-notebook-2',  // 优化：使用笔记本图标
        '开发工具': 'el-icon-setting',
        '设计资源': 'el-icon-picture',
        '办公软件': 'el-icon-files',
        '游戏娱乐': 'el-icon-trophy',
        '学习资料': 'el-icon-reading',
        'AI智能': 'el-icon-cpu',  // 新增：AI智能分类
        'AI': 'el-icon-cpu',  // 简短别名
        '人工智能': 'el-icon-cpu'  // 别名
      }
      return iconMap[categoryName] || 'el-icon-folder'
    },
    getCategoryColor(categoryName) {
      const colorMap = {
        '电脑软件': '#409EFF',
        '手机软件': '#67C23A',
        '精品网站': '#E6A23C',
        '视频教程': '#F56C6C',
        '图文教程': '#9B59B6',  // 优化：使用紫罗兰色，更有设计感
        '开发工具': '#8E44AD',
        '设计资源': '#E74C3C',
        '办公软件': '#3498DB',
        '游戏娱乐': '#F39C12',
        '学习资料': '#16A085',
        'AI智能': '#00D4FF',  // 新增：科技蓝色
        'AI': '#00D4FF',
        '人工智能': '#00D4FF'
      }
      return colorMap[categoryName] || '#606266'
    }
  },
  computed: {
    gridStyle() {
      if (this.displayMode === 'grid') {
        const minWidth = this.gridColumns === 2 ? '400px' : 
                        this.gridColumns === 3 ? '280px' : 
                        this.gridColumns === 4 ? '240px' : 
                        this.gridColumns === 5 ? '200px' : '180px'
        return {
          gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}, 1fr))`
        }
      }
      return {}
    }
  }
}
</script>

<style scoped>
.home {
  padding: 20px 0;
  max-width: 1400px;
  margin: 0 auto;
}

/* 分类导航 */
.category-nav {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 252, 0.98) 100%);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.08), 0 2px 8px rgba(0, 0, 0, 0.02);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.6);
}

.category-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(245, 247, 250, 0.95) 100%);
  border: 1.5px solid rgba(var(--theme-primary-rgb, 102, 126, 234), 0.1);
  border-radius: 28px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color, #606266);
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.category-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
  transition: left 0.6s;
}

.category-item:hover::before {
  left: 100%;
}

.category-item i {
  font-size: 20px;
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.category-icon-all {
  color: var(--theme-primary, #667eea);
  filter: drop-shadow(0 2px 4px rgba(var(--theme-primary-rgb, 102, 126, 234), 0.3));
}

.category-item:hover {
  background: linear-gradient(135deg, rgba(236, 245, 255, 1) 0%, rgba(255, 255, 255, 1) 100%);
  border-color: rgba(var(--theme-primary-rgb, 102, 126, 234), 0.4);
  transform: translateY(-4px) scale(1.03);
  box-shadow: 0 8px 24px rgba(var(--theme-primary-rgb, 102, 126, 234), 0.2), 0 4px 12px rgba(0, 0, 0, 0.05);
}

.category-item:hover i {
  transform: scale(1.2) rotate(5deg);
  filter: brightness(1.2);
}

.category-item.active {
  background: linear-gradient(135deg, var(--theme-primary, #667eea) 0%, var(--theme-primary-dark, #764ba2) 100%);
  color: white;
  border-color: transparent;
  box-shadow: 0 8px 28px rgba(var(--theme-primary-rgb, 102, 126, 234), 0.45), 0 4px 12px rgba(var(--theme-primary-dark-rgb, 118, 75, 162), 0.3);
  transform: translateY(-3px) scale(1.02);
}

.category-item.active i {
  color: white !important;
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.3));
  animation: iconBounce 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes iconBounce {
  0%, 100% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.3) rotate(-10deg); }
  50% { transform: scale(1.15) rotate(5deg); }
  75% { transform: scale(1.25) rotate(-5deg); }
}

.category-item.active:hover {
  transform: translateY(-5px) scale(1.05);
  box-shadow: 0 12px 32px rgba(var(--theme-primary-rgb, 102, 126, 234), 0.5), 0 6px 16px rgba(var(--theme-primary-dark-rgb, 118, 75, 162), 0.35);
}

/* 深色模式优化 */
.dark-mode .category-nav {
  background: linear-gradient(135deg, rgba(42, 42, 42, 0.95) 0%, rgba(32, 32, 32, 0.98) 100%);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(255, 255, 255, 0.02);
  border-color: rgba(255, 255, 255, 0.05);
}

.dark-mode .category-item {
  background: linear-gradient(135deg, rgba(42, 42, 42, 0.8) 0%, rgba(50, 50, 50, 0.9) 100%);
  border-color: rgba(var(--theme-primary-rgb, 102, 126, 234), 0.15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.dark-mode .category-item:hover {
  background: linear-gradient(135deg, rgba(64, 158, 255, 0.2) 0%, rgba(var(--theme-primary-rgb, 102, 126, 234), 0.15) 100%);
  border-color: rgba(var(--theme-primary-rgb, 102, 126, 234), 0.5);
  box-shadow: 0 8px 24px rgba(var(--theme-primary-rgb, 102, 126, 234), 0.3), 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* 深色模式资源卡片 - 必须在默认样式之后 */
.dark-mode .resource-item {
  background: linear-gradient(135deg, rgba(42, 42, 42, 0.95) 0%, rgba(35, 35, 35, 0.98) 100%) !important;
  border-color: rgba(102, 126, 234, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(255, 255, 255, 0.02);
}

.dark-mode .resource-item:hover {
  background: linear-gradient(135deg, rgba(50, 50, 50, 0.98) 0%, rgba(40, 40, 40, 1) 100%);
  border-color: var(--primary-color);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.25), 0 6px 16px rgba(0, 0, 0, 0.4);
}

.dark-mode .item-header {
  border-bottom-color: rgba(102, 126, 234, 0.15);
}

.dark-mode .item-title {
  color: var(--theme-text);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.dark-mode .resource-item:hover .item-title {
  color: var(--theme-text);
}

.dark-mode .stat-item:hover {
  color: var(--theme-text) !important;
  background: rgba(var(--theme-text-rgb, 248, 245, 245), 0.1);
}

.dark-mode .item-desc {
  color: var(--theme-text);
}

.dark-mode .item-footer {
  border-top-color: rgba(102, 126, 234, 0.1);
}

.dark-mode .resource-card {
  background: linear-gradient(135deg, rgba(42, 42, 42, 0.95) 0%, rgba(35, 35, 35, 0.98) 100%) !important;
  border-color: rgba(102, 126, 234, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.dark-mode .resource-card:hover {
  background: linear-gradient(135deg, rgba(50, 50, 50, 0.98) 0%, rgba(40, 40, 40, 1) 100%);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.25), 0 6px 16px rgba(0, 0, 0, 0.4);
}

.dark-mode .card-title {
  color: var(--theme-text);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.dark-mode .resource-card:hover .card-title {
  color: var(--theme-text);
}

.dark-mode .card-desc {
  color: var(--theme-text);
}

.dark-mode .card-footer {
  border-top-color: rgba(102, 126, 234, 0.1);
}

/* 资源列表*/
.resource-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 200px; /* 调整最小高度，避免影响资源项显示 */
}

.resource-list.two-columns {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

/* 资源项目 */
.resource-item {
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 252, 0.98) 100%);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 14px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(31, 38, 135, 0.08), 0 2px 8px rgba(0, 0, 0, 0.02);
  border: 1.5px solid rgba(102, 126, 234, 0.08);
  position: relative;
  overflow: hidden;
}

.resource-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.05), transparent);
  transition: left 0.6s;
}

.resource-item:hover::before {
  left: 100%;
}

.resource-item:hover {
  transform: translateY(-4px) scale(1.01);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.15), 0 6px 16px rgba(0, 0, 0, 0.08);
  border-color: rgba(102, 126, 234, 0.3);
}

/* 标题区域 */
.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  padding-right: 12px;
  position: relative;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
}

.item-title {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0.3px;
  transition: color 0.3s;
  max-width: calc(100% - 320px);
}

.resource-item:hover .item-title {
  color: var(--theme-primary, #667eea);
}

.item-tags {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
  align-items: center;
  min-width: 300px;
  justify-content: flex-end;
  flex-wrap: nowrap;
}

/* 置顶标签 */
.top-badge {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  padding: 5px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 4px 12px rgba(245, 87, 108, 0.4);
  animation: badgePulse 2s infinite;
}

@keyframes badgePulse {
  0%, 100% { box-shadow: 0 4px 12px rgba(245, 87, 108, 0.4); }
  50% { box-shadow: 0 6px 20px rgba(245, 87, 108, 0.6); }
}

/* 内容主体：图片和描述左右排列 */
.item-body {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

/* 封面图 */
.item-cover {
  flex-shrink: 0;
  width: 150px;
  height: 110px; /* 固定高度 */
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eaf0 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  position: relative;
  align-self: flex-start;
}

.item-cover::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  opacity: 0;
  transition: opacity 0.4s;
}

.resource-item:hover .item-cover::after {
  opacity: 1;
}

.item-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.resource-item:hover .item-cover img {
  transform: scale(1.08) rotate(1deg);
}

/* 描述和统计区域 */
.item-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.item-desc {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  margin: 0 0 12px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 21px; /* 1行文本的最小高度 */
}

.item-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid rgba(102, 126, 234, 0.08);
}

.item-stats {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #909399;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s;
  padding: 4px 8px;
  border-radius: 8px;
}

.stat-item:hover {
  color: var(--theme-primary, #667eea);
  background: rgba(var(--theme-primary-rgb, 102, 126, 234), 0.08);
}

.stat-item i {
  font-size: 15px;
  transition: transform 0.3s;
}

.stat-item:hover i {
  transform: scale(1.2);
}

.item-time {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  background: rgba(144, 147, 153, 0.08);
  border-radius: 8px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .home {
    padding: 12px;
  }
  
  /* 分类导航移动端优化 */
  .category-nav {
    padding: 12px;
    gap: 8px;
    margin-bottom: 12px;
    border-radius: 12px;
  }
  
  .category-item {
    padding: 8px 14px;
    font-size: 13px;
    border-radius: 20px;
    gap: 6px;
  }
  
  .category-item i {
    font-size: 16px; /* 图标稍微缩小 */
  }
  
  /* 移动端取消hover效果 */
  .category-item:hover {
    transform: none;
  }
  
  .category-item.active {
    transform: none;
  }
  
  .category-item.active:hover {
    transform: none;
  }
  
  /* 列表模式移动端优化 */
  .item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .item-title {
    max-width: 100%; /* 移动端占满宽度 */
    white-space: normal;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }
  
  .item-tags {
    width: 100%; /* 占满宽度 */
    min-width: 0; /* 移除最小宽度限制 */
    flex-wrap: wrap; /* 允许换行 */
    justify-content: flex-start; /* 左对齐 */
  }
  
  .item-body {
    flex-direction: column;
  }
  
  .item-cover {
    width: 100%;
    height: 200px; /* 移动端固定高度 */
  }
  
  /* 网格模式移动端优化 */
  .resource-list.two-columns {
    grid-template-columns: 1fr;
  }
  
  .card-tags {
    flex-wrap: wrap; /* 允许标签换行 */
    max-height: none; /* 移除高度限制 */
  }
  
  .card-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .card-stats {
    width: 100%;
    justify-content: space-between;
  }
}

/* ==========  网格模式样式 ========== */
.resource-grid {
  display: grid;
  gap: 24px;
  min-height: 400px;
}

.resource-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 252, 0.98) 100%);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(31, 38, 135, 0.08), 0 2px 8px rgba(0, 0, 0, 0.02);
  position: relative;
  border: 1.5px solid rgba(102, 126, 234, 0.08);
}

.resource-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 16px 40px rgba(102, 126, 234, 0.2), 0 8px 20px rgba(0, 0, 0, 0.1);
  border-color: rgba(102, 126, 234, 0.3);
}

.top-badge-card {
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: #fff;
  border-bottom-left-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(245, 87, 108, 0.35);
  pointer-events: none;
  z-index: 12;
}

.top-corner-badge {
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: #fff;
  border-bottom-left-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(245, 87, 108, 0.35);
  pointer-events: none;
  z-index: 12;
}

.top-corner-badge i {
  font-size: 14px;
}

.top-corner-badge i {
  font-size: 12px;
}

.top-corner-badge::before { content: none; }

.top-corner-badge::after { content: none; }


.card-cover {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eaf0 100%);
}

.card-cover::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
  opacity: 0;
  transition: opacity 0.4s;
}

.resource-card:hover .card-cover::after {
  opacity: 1;
}

.card-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.resource-card:hover .card-cover img {
  transform: scale(1.15) rotate(2deg);
}

.cover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.85) 0%, rgba(118, 75, 162, 0.9) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.4s;
  font-size: 16px;
  font-weight: 600;
}

.resource-card:hover .cover-overlay {
  opacity: 1;
}

.cover-overlay i {
  font-size: 40px;
  margin-bottom: 10px;
  animation: iconFloat 2s infinite;
}

@keyframes iconFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.card-content {
  padding: 20px;
}

.card-title {
  font-size: 17px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 10px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0.3px;
  transition: color 0.3s;
}

.resource-card:hover .card-title {
  color: #667eea;
}

.card-desc {
  font-size: 13px;
  color: #909399;
  line-height: 1.7;
  margin: 0 0 14px 0;
  height: 44px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 14px;
  border-top: 1px solid rgba(102, 126, 234, 0.08);
}

.card-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  max-height: 60px;
  overflow: hidden;
}

.resource-tag {
  margin-top: 2px;
  flex-shrink: 0;
}

.more-tags {
  font-size: 12px;
  color: #909399;
  background: #f5f7fa;
  padding: 0 8px;
  border-radius: 10px;
  height: 24px;
  line-height: 24px;
  flex-shrink: 0;
}

.more-tags-mini {
  font-size: 12px;
  color: #909399;
  background: #f5f7fa;
  padding: 0 6px;
  border-radius: 8px;
  height: 20px;
  line-height: 20px;
  flex-shrink: 0;
}

.card-stats {
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: #909399;
}

.card-stats .stat-item {
  padding: 3px 6px;
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 16px 0;
  background: transparent;
}

/* 深色模式分页样式 */
.dark-mode .pagination-wrapper {
  background: transparent;
}

.dark-mode .card-title {
  color: var(--theme-text);
}

.dark-mode .resource-card:hover .card-title {
  color: var(--theme-text);
}

.dark-mode >>> .el-pagination {
  background: transparent;
}

.dark-mode >>> .el-pagination.is-background .btn-prev,
.dark-mode >>> .el-pagination.is-background .btn-next,
.dark-mode >>> .el-pagination.is-background .el-pager li {
  background-color: rgba(42, 42, 42, 0.95) !important;
  color: var(--theme-text);
  border: 1.5px solid rgba(102, 126, 234, 0.15);
  transition: all 0.3s;
}

.dark-mode >>> .el-pagination.is-background .btn-prev:hover,
.dark-mode >>> .el-pagination.is-background .btn-next:hover,
.dark-mode >>> .el-pagination.is-background .el-pager li:not(.disabled):hover {
  color: var(--theme-text);
  border-color: rgba(var(--theme-text-rgb, 248, 245, 245), 0.3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--theme-text-rgb, 248, 245, 245), 0.15);
}

.dark-mode >>> .el-pagination.is-background .el-pager li:not(.disabled).active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white;
  border-color: transparent;
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

/* 骨架屏样式 */
.skeleton-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.skeleton-item {
  display: flex;
  gap: 12px;
  padding: 14px;
  border-radius: 10px;
  background: var(--skeleton-bg, #f5f7fa);
}
.skeleton-cover {
  width: 120px;
  height: 80px;
  border-radius: 8px;
  background: #eaeef2;
}
.skeleton-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.skeleton-line {
  height: 14px;
  border-radius: 7px;
  background: #eaeef2;
}
.shimmer {
  position: relative;
  overflow: hidden;
}
.shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150px;
  width: 150px;
  height: 100%;
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 100%);
  animation: shimmerMove 1.2s infinite;
}
@keyframes shimmerMove {
  0% { left: -150px; }
  100% { left: 100%; }
}

/* 骨架屏深色模式 */
.dark-mode .skeleton-item {
  background: #2a2a2a;
}

.dark-mode .skeleton-cover {
  background: #1a1a1a;
}

.dark-mode .skeleton-line {
  background: #1a1a1a;
}

.dark-mode .shimmer::after {
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0) 100%);
}

/* 资源列表淡入淡出动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.fade-enter {
  opacity: 0;
  transform: translateY(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
</style>
